import { debounce, get, isEmpty, isEqual, isNil, isNull, isUndefined, set, unionBy, uniqBy } from 'lodash'
import { EMPTY_DROPDOWN_LIST, VARIABLE_TYPES, convertOptions, isObject } from '../utils'
import { convertStringToRegExpSearchString } from './regular-expressions'
import { SOURCE_TYPES } from '../components/list-new/lib/list-constants'
import { PANEL_TYPES } from '../components/layout-manager/gui-editor/options/schema'

export default {
  props: {
    computedReadOnly: {
      type: Boolean,
      default: false,
    },
    field: {
      type: Object,
      default: () => ({}),
    },
    filterExp: {
      type: Object,
      default: () => ({}),
    },
    idForm: {
      type: String,
      default: '',
    },
    idQuery: {
      type: String,
      default: '',
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    list: {
      type: Object,
      default: () => EMPTY_DROPDOWN_LIST,
    },
    path: {
      type: String,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    localizeData: {
      type: Boolean,
      default: false,
    },
    hasKeyAsString: {
      type: Boolean,
      default: false,
    },
    dropdownOnlyOne: {
      type: Boolean,
      default: true,
    },
    rawSqlId: {
      type: String,
      default: '',
    },
    formName: {
      type: String,
    },
    fieldNameProp: {
      type: String,
    },
  },
  data() {
    return {
      page: 0,
      initial: true,
      lastQuery: '',
      lastRequestQuery: '',
      lastPageOfOptions: [],
      dynamicOptions: [],
      isOpen: false,
      optionLoader: false,
      fieldLoader: false,
      loadingText: this.$i18n.t('loading'),
      popper: null,
      lastOption: null,
      scrollArrow: null,
      completed: false,
      mounted: false,
    }
  },
  computed: {
    singleSelectId() {
      return `${this.fieldNameProp}-${this.name}-datalist`
    },
    dropdownHasValue() {
      if (isObject(this.value) || isNull(this.value) || isUndefined(this.value)) return false
      return !!String(this.value).trim()
    },
    externalParams() {
      return JSON.stringify(Object.assign(this.$route.params, this.$CORE.externalParams, this.$CORE.params))
    },
    fieldName() {
      const data = get(this.field, 'data')
      const name = get(this.field, 'name')
      const label = get(this.field, 'label')
      return data || name || label
    },
    fieldId() {
      const id = get(this.field, 'id')
      const fieldId = get(this.field, 'fieldId')
      return id || fieldId
    },
    filteredOptions() {
      if (!this.query) return this.options
      const query = convertStringToRegExpSearchString(this.query)
      const queryRegExp = new RegExp(query, 'mi')
      return this.options.filter((option) => {
        if (!option || !option.value) return false
        const stringValue = String(option.value)
        return stringValue.match(queryRegExp)
      })
    },
    getListExpressionContextData() {
      return this.isFormControl ? this.$get('getListExpressionContextData')(this.path) : null
    },
    getRawSqlId() {
      return this.rawSqlId || (typeof this.$get === VARIABLE_TYPES.FUNCTION && this.$get('rawSqlId')) || null
    },
    hasAllOptions() {
      return (!this.initial || get(this.list, 'options', []).length) && this.lastRequestQuery === '' && this.completed
    },
    //@TODO ha majd egyszer minden dropdownunk folott lesz store, akkor ezeket az ellenorzeseket ki lehet irtani
    idSource() {
      return typeof this.$get === VARIABLE_TYPES.FUNCTION ? this.$get('idSource')(this.path) : this.getIdSource
    },
    isFormControl() {
      return !!(this.$storeNamespace && this.$storeNamespace.match(/^form/)) // @regex matches 'form' at start of string
    },
    isSearchControl() {
      return !!(this.isFilter || this.isTemplatedListControl)
    },
    isTemplatedListControl() {
      return this.$get && this.$get('componentType') === PANEL_TYPES.TEMPLATED_LIST
    },
    listExp() {
      return get(this.field, 'listExp', null)
    },
    missingOptions() {
      let missing = []
      if (this.dropdownHasValue) {
        this.valueArray.forEach((value) => {
          const optionFound = this.options.find((option) => String(option.key) === String(value))
          if ((this.taggable && !isObject(value) && !optionFound) || (!this.taggable && !optionFound)) {
            missing.push(value)
          }
        })
      }
      return missing
    },
    name() {
      return get(this.field, 'name', '')
    },
    noDataText() {
      return this.optionLoader ? this.$i18n.t('searching_results') : this.$i18n.t('no_result')
    },
    options: {
      get() {
        return this.dynamicOptions
      },
      set(options) {
        this.dynamicOptions = options

        const isSubFormPath = this.path && this.path.split('/').length > 4
        const isNewSubFormRow = isSubFormPath && !this.isOpen

        if (this.path && Array.isArray(options) && this.mounted && !this.initial && !isNewSubFormRow) {
          this.$dispatch('updatePropByPath', {
            path: this.path,
            prop: 'dynamicOptions',
            value: [...options],
            byExpression: true,
            formName: this.formName,
          })
        }
      },
    },
    placeholder() {
      if (this.query || this.valueArray.length) return ''
      if (this.computedReadOnly) {
        return this.$i18n.t('not_set')
      }
      if (this.taggable && !this.optionsToRender.length) {
        return this.$i18n.t('start_typing')
      }
      if (!this.filteredOptions.length) {
        return this.$i18n.t('start_typing')
      }
      return this.multiple ? this.$i18n.t('select_items') : this.$i18n.t('select_item')
    },
    /**
     * @description A szurobe irt szoveg
     * @returns {boolean|string}
     */
    query: {
      get() {
        return this.mounted && this.isOpen && get(this.$refs, ['infinite-dropdown', 'query'], '')
      },
      set(value) {
        if (this.isSingleSelect) {
          this.singleSelectQuery = value
        }
      },
    },
    sourceType() {
      return (typeof this.$get === VARIABLE_TYPES.FUNCTION && this.$get('sourceType')) || this.getSourceType
    },
    getSourceType() {
      if (this.idForm) return SOURCE_TYPES.FORM
      if (this.idQuery) return SOURCE_TYPES.QUERY
    },
    selectedOptions() {
      return this.options.filter((option) => this.valueArray.includes(option.value))
    },
    selectionValue() {
      return this.dropdownHasValue ? this.valueArray : null
    },
    valueArray() {
      if (isNull(this.value) || isUndefined(this.value)) return []
      return Array.isArray(this.value) ? this.value : [this.value]
    },
    value: {
      get() {
        const value = get(this.field, 'value', this.multiple ? [] : null)
        if (this.multiple && !isNil(value) && !Array.isArray(value)) return [value]
        return value
      },
      set(value) {
        const options =
          (Array.isArray(this.options) &&
            this.options.filter(({ key }) => {
              if (this.multiple) {
                return value.includes(key)
              } else {
                return value === key
              }
            })) ||
          []
        this.$emit('change', { value, options })
      },
    },
  },
  methods: {
    setDropdownQuery(value) {
      this.$set(this, 'query', value)
      set(this.$refs, ['infinite-dropdown', 'query'], value)
    },
    /**
     * @description Tovabbi elemeket jelzo nyilat tesz a dropdownra
     * @param el
     */
    addScrollArrow(el) {
      el.appendChild(this.scrollArrow)
    },
    addScrollEventToPopper() {
      const el = get(this.$refs, ['infinite-dropdown', '$refs', 'popper', '$el'], document.createElement('span'))
      this.popper = el.querySelector('.el-select-dropdown__wrap')
      if (this.popper) {
        this.popper.addEventListener('scroll', this.onScrollOptions)
      }
    },
    /**
     *  Cancel autocomplete select field readonly for mobile view
     **/
    cancelReadOnly(visible) {
      this.$nextTick(() => {
        if (!visible) {
          const select = this.$refs['infinite-dropdown']
          if (select) {
            const input = select.$el.querySelector('.el-input__inner')
            input.removeAttribute('readonly')
          }
        }
      })
    },
    checkMissingOptions() {
      if (!this.fieldLoader && this.missingOptions.length) {
        this.page = 0
        this.initial = false
        this.options = this.options.filter(({ key }) => this.valueArray.includes(key))
        this.getOptions()
      }
    },
    /**
     * @description Elokesziti a tovabbi elemeket jelzo nyilat egy DOM elementbe
     */
    createArrowElement() {
      this.scrollArrow = document.createElement('div')
      this.scrollArrow.setAttribute('id', 'scroll-arrow')
      this.scrollArrow.classList.add('material-icons', 'md-16', 'w-100', 'text-muted', 'text-center')
      this.scrollArrow.innerHTML = 'arrow_drop_down'
    },
    /**
     * @description Az elementes selectben levo input mezo beiraskor nem emittel elementen kivulre,
     * muszaj sajat listenert aggatni ra
     */
    createInputListener() {
      const refString = this.multiple ? 'input' : 'reference'
      const inputElement = get(this.$refs, ['infinite-dropdown', '$refs', refString, '$el'], null)
      if (inputElement) inputElement.addEventListener('input', this.onReferencedInput)
    },
    filterOptions() {
      return this.filteredOptions
    },
    /**
     * @description Lekerdez egy adagot a dropdown elemeibol a parameterek alapjan
     * @returns {Promise<void>}
     */
    async getOptions() {
      this.optionLoader = true
      const parameters = {
        sourceType: this.sourceType,
        idSource: this.idSource,
        fieldId: this.fieldId,
        fieldName: this.fieldName,
        filter: this.isOpen ? this.query : null,
        isSearchControl: this.isSearchControl,
        contextData: this.getListExpressionContextData,
        externalParams: this.externalParams,
        selection: this.selectionValue ? JSON.stringify(this.selectionValue) : null,
        page: Number(this.page),
      }
      const response = await this.$client.execute({
        operationId: 'LazyLoader_bySourceType',
        parameters,
      })
      const { options = [], completed = true } = get(response, 'body', EMPTY_DROPDOWN_LIST)
      this.lastPageOfOptions = convertOptions(this.$i18n, options, this.localizeData)
      this.completed = completed
      if (!completed) this.page++
      this.options = uniqBy(unionBy(this.options, this.lastPageOfOptions, 'key'), 'key')
      if (this.missingOptions.length) this.removeMissingOptions()
      this.optionLoader = false
      this.fieldLoader = false
      this.$nextTick(() => {
        this.refreshLastOption()
        this.refreshScrollArrow()
      })
    },
    removeMissingOptions() {
      if (Array.isArray(this.value)) {
        const newValue = [...this.value]
        for (const option of [...this.missingOptions]) {
          newValue.splice(newValue.indexOf(option), 1)
        }
        this.value = newValue
      } else {
        this.value = null
      }
    },
    onReferencedInput({ target }) {
      if (!this.isOpen) {
        this.$refs['infinite-dropdown'].toggleMenu()
        this.$nextTick(() => {
          this.$refs['infinite-dropdown'].currentPlaceholder = this.$refs['infinite-dropdown'].cachedPlaceHolder
        })
      }
      this.query = target.value
    },
    /**
     * @description Dropdown szuresekor az elemek lekerdezeset kezeli debounce-szal
     */
    debouncedDataFetch: debounce(function () {
      if (this.lastRequestQuery !== this.query) {
        this.lastRequestQuery = this.query || ''
        this.options = []
      }
      this.completed = false
      this.page = 0
      this.getOptions()
    }, 500),
    isNarrowDown(newQuery) {
      if (this.hasAllOptions) return true
      if (!newQuery || isEmpty(newQuery)) return false
      if (
        typeof newQuery !== VARIABLE_TYPES.STRING &&
        typeof newQuery !== VARIABLE_TYPES.NUMBER &&
        !Array.isArray(newQuery)
      ) {
        if (!this.$CORE.isEnvProduction) {
          console.error(`🚑Furcsa szuroertek: ${JSON.stringify(newQuery)} type: ${typeof newQuery}`)
        }
        return false
      }
      this.lastQuery = String(newQuery)
      const isNarrowing = this.lastRequestQuery && String(newQuery).includes(this.lastRequestQuery)
      return isNarrowing && this.completed
    },
    onClear() {
      if (!this.hasAllOptions) {
        this.initial = true
        this.page = 0
      }
      this.value = null
    },
    /**
     * @description Ha a dropdown lista propja valtozik, felulirjuk az options-t a kapott elemekkel,
     *              'Dropdownlonlyone' eseten beallitjuk a mezo erteket, ha egy elemu a lista
     */
    onListChanged() {
      this.options = uniqBy(get(this.list, 'options', []), 'key')
      this.completed = get(this.list, 'completed')
      if (this.dropdownOnlyOne && this.options.length === 1) {
        const value = this.multiple || this.taggable ? [this.options[0].key] : this.options[0].key
        if (isEqual(this.value, value)) return
        this.$emit('form-item-changed', { value, byExpression: true })
        this.$emit('change', { value, byExpression: true })
      } else if (this.missingOptions.length) {
        this.$emit('form-item-changed', { value: this.multiple ? [] : null, byExpression: false })
        this.$emit('change', { value: this.multiple ? [] : null, byExpression: false })
      }
    },
    /**
     * @description Dropdown gorgetesekor, ha az aljara ertunk, kovetkezo adag optiont kerdez le
     * @returns {Promise<void>}
     */
    async onScrollOptions() {
      if (!this.lastOption || !this.popper) return
      const popperBottom = this.popper.getBoundingClientRect().bottom
      const optionTop = this.lastOption.getBoundingClientRect().top
      if (!this.optionLoader && popperBottom > optionTop && !this.completed) {
        await this.getOptions()
      }
    },
    /**
     * @description Dropdown lenyitaskor a popperre listenert rak gorgeteshez, es frissiti a tovabbi elemeket jelzo nyilat
     * @param visible
     * @returns {Promise<void>}
     */
    async onVisibleChange(visible) {
      this.isOpen = visible
      this.cancelReadOnly()
      if (visible) {
        if (this.initial && !this.completed) {
          this.initial = false
          await this.getOptions()
        }
        this.addScrollEventToPopper()
        setTimeout(async () => {
          this.refreshLastOption()
          await this.onScrollOptions()
          this.refreshScrollArrow()
        }, 500)
      }
      if (!visible) {
        this.removeScrollEventFromPopper()
      }
    },
    /**
     * @description Dropdown utolso elem hivatkozasat frissiti
     */
    refreshLastOption() {
      if (this.popper) {
        this.lastOption = this.popper.querySelector('ul > li.el-select-dropdown__item:last-of-type')
      }
    },
    /**
     * @description Tovabbi elemeket jelzo nyil megjeleneset frissiti
     */
    refreshScrollArrow() {
      const el = get(this.$refs, ['infinite-dropdown', '$refs', 'scrollbar', '$el'])
      if (el) {
        const arrow = el.querySelector('#scroll-arrow')
        if (arrow && this.completed) {
          this.removeScrollArrow(el)
        } else if (!arrow && !this.completed) {
          this.addScrollArrow(el)
        }
      }
    },
    /**
     * @description Leveszi a tovabbi elemeket jelzo nyilat
     * @param el
     */
    removeScrollArrow(el) {
      el.removeChild(el.lastElementChild)
    },
    removeScrollEventFromPopper() {
      if (this.popper) {
        this.popper.removeEventListener('scroll', this.onScrollOptions)
        this.popper = null
      }
    },
    setOptions() {
      this.options = uniqBy(get(this.list, 'options', []), 'key')
      this.completed = get(this.list, 'completed', true)
    },
    valueWatchHandler(newValue, oldValue) {
      if (!isEqual(newValue, oldValue) && this.dropdownHasValue) {
        this.checkMissingOptions()
      }
    },
  },
  mounted() {
    this.mounted = true
    this.setOptions()
    this.createArrowElement()
    this.$nextTick(() => {
      this.checkMissingOptions()
      this.createInputListener()
    })
    // @ todo vajon hol kapunk meg url-t lista helyett?
    if (typeof this.list === 'string' && this.list.length) {
      if (!this.$CORE.isEnvProduction) {
        console.error('ezt a cuccot vizsgald ki, mixin-dropdown mounted', this.list)
        debugger
      }
    }
  },
  beforeDestroy() {
    EventBus.$off(`query-dropdown-items-${this.singleSelectId}`, this.setDropdownQuery)
  },
  watch: {
    'field.hidden': {
      immediate: true,
      handler(isHidden) {
        if (!isHidden) {
          EventBus.$on(`query-dropdown-items-${this.singleSelectId}`, this.setDropdownQuery)
        } else {
          EventBus.$off(`query-dropdown-items-${this.singleSelectId}`, this.setDropdownQuery)
        }
      },
    },
    value: {
      deep: true,
      handler(newValue, oldValue) {
        this.valueWatchHandler(newValue, oldValue)
      },
    },
    computedReadOnly: {
      handler(newValue) {
        if (!newValue) {
          this.$nextTick(() => {
            this.createInputListener()
          })
        }
      },
    },
    list: {
      deep: true,
      async handler(newVal, oldVal) {
        if (!isEqual(newVal, oldVal)) {
          this.onListChanged()
        }
      },
    },
    query: {
      handler(newValue, oldValue) {
        if (this.isNarrowDown(newValue) || oldValue === false || newValue === false) return
        if (newValue === this.content) return
        this.debouncedDataFetch()
      },
    },
    missingOptions: {
      handler(newValue, oldValue) {
        if (this.optionLoader) return
        if (!isEmpty(newValue) && !isEqual(newValue, oldValue)) {
          this.getOptions()
        }
      },
    },
  },
}
